import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  LayoutDashboard,
  Building2,
  Calendar,
  Users,
  BarChart3,
  Settings,
  Mail,
  LogOut,
  FileText,
  Package,
  ChevronDown,
  ChevronRight,
  LineChart,
} from "lucide-react";

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isReportsOpen, setIsReportsOpen] = useState(false);

  // Define all navigation items with their required roles
  const allNavigation = [
    {
      name: "Dashboard",
      href: "/dashboard",
      icon: LayoutDashboard,
      roles: [
        "Super Admin",
        "Hotel Manager",
        "Booking Manager",
        "Reports Viewer",
      ],
    },
    {
      name: "Hotels",
      href: "/hotels",
      icon: Building2,
      roles: ["Super Admin", "Hotel Manager"],
    },
    {
      name: "Bookings",
      href: "/bookings",
      icon: Calendar,
      roles: ["Super Admin", "Booking Manager"],
    },
    { name: "Users", href: "/users", icon: Users, roles: ["Super Admin"] },
  ];

  // Define reports submenu items
  const reportsSubmenu = [
    {
      name: "Booking Reports",
      href: "/reports/bookings",
      icon: FileText,
      roles: ["Super Admin", "Reports Viewer"],
    },
    {
      name: "Inventory Reports",
      href: "/reports/inventory",
      icon: Package,
      roles: ["Super Admin", "Reports Viewer"],
    },
    {
      name: "Hotel Performance",
      href: "/reports/hotel-performance",
      icon: LineChart,
      roles: ["Super Admin", "Reports Viewer"],
    },
  ];

  // Filter navigation based on user's role
  const navigation = allNavigation.filter((item) => {
    if (!user?.role_name) {
      console.log("No role_name found for user:", user);
      return false;
    }
    const hasAccess = item.roles.includes(user.role_name);
    console.log(
      `Checking ${item.name}: user role=${
        user.role_name
      }, allowed roles=${item.roles.join(",")}, hasAccess=${hasAccess}`
    );
    return hasAccess;
  });

  // Check if user has access to reports
  const hasReportsAccess =
    user?.role_name &&
    ["Super Admin", "Reports Viewer"].includes(user.role_name);

  // Check if any reports submenu item is active
  const isReportsActive = reportsSubmenu.some(
    (item) => location.pathname === item.href
  );

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="flex h-screen w-64 flex-col bg-white border-r border-border shadow-sm">
      {/* Logo */}
      <div className="flex h-16 items-center px-6 border-b border-border bg-white">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <Building2 className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="text-xl font-bold text-foreground">
            TRP Extranet
          </span>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 px-4 py-4 space-y-2 bg-white">
        <div className="space-y-1">
          {navigation.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.href)
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-secondary hover:text-secondary-foreground"
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{item.name}</span>
              </Link>
            );
          })}

          {/* Reports Menu with Submenu */}
          {hasReportsAccess && (
            <div className="space-y-1">
              <button
                onClick={() => setIsReportsOpen(!isReportsOpen)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isReportsActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-secondary hover:text-secondary-foreground"
                }`}
              >
                <div className="flex items-center space-x-3">
                  <BarChart3 className="w-5 h-5" />
                  <span>Reports</span>
                </div>
                {isReportsOpen ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>

              {/* Reports Submenu */}
              {isReportsOpen && (
                <div className="ml-6 space-y-1">
                  {reportsSubmenu.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isActive(item.href)
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground hover:bg-secondary hover:text-secondary-foreground"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{item.name}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Bottom Section */}
      {/* Settings menu hidden - no functionality connected */}
      {/* <div className="p-4 border-t border-border space-y-2 bg-white">
        <button className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-secondary-foreground transition-colors">
          <Settings className="w-5 h-5 text-muted-foreground" />
          <span>Settings</span>
        </button>
      </div> */}

      {/* User Profile */}
      <div className="p-4 border-t border-border bg-white">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-primary">
              {user?.username?.charAt(0).toUpperCase() || "A"}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">
              {user?.username || "Admin User"}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {user?.email || "admin@trpextranet.com"}
            </p>
          </div>
          <button
            onClick={logout}
            className="text-muted-foreground hover:text-primary transition-colors"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
